import { Component } from "@theme/component";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { Cart } from '@theme/cart';
import { CartRecommended } from '@theme/cart-recommended';

const CUSTOM_DOZEN_VARIANT_ID = window.location.href.includes("georgetown-cupcake-development.myshopify.com") ? "41451295047813" : '47500743114979';
const HALF_DOZEN_VARIANT_ID = window.location.href.includes("georgetown-cupcake-development.myshopify.com") ? "41451296489605" : '47604202012899';


/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange
    );
    this.setMultipleRecipientsClick();

    this.cart = new Cart(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange
    );
    this.cart?.disconnect();
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const {
      quantity,
      cartLine: line,
      isFlavor,
      variantId,
      inventoryGeorgetown,
      inventoryBethesda,
      inventoryNationwide,
      date,
      flavors,
      locationValue,
      validate,
      prevValue,
    } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      isFlavor,
      variantId,
      inventoryGeorgetown,
      inventoryBethesda,
      inventoryNationwide,
      date,
      flavors,
      locationValue,
      validate,
      prevValue,
      action: "change",
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector("text-component")
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: "clear",
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter(
        (row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key
      ),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty("--row-height", `${row.clientHeight}px`);
      row.classList.add("removing");

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Decodes HTML entities in a string.
   * @param {string} text - The text with HTML entities.
   * @returns {string} The decoded text.
   */
  #decodeHTMLEntities(text) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {boolean} [config.isFlavor]
   * @param {string} [config.variantId] - The variant ID.
   * @param {number} [config.inventoryGeorgetown] - The inventory Georgetown.
   * @param {number} [config.inventoryBethesda] - The inventory Bethesda.
   * @param {number} [config.inventoryNationwide] - The inventory Nationwide.
   * @param {string} [config.date] - The date of the order.
   * @param {string} [config.flavors] - The flavors of the item.
   * @param {string} [config.locationValue] - The location value.
   */

  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();
    const {
      line,
      quantity,
      isFlavor,
      variantId,
      inventoryGeorgetown,
      inventoryBethesda,
      inventoryNationwide,
      date,
      flavors,
      locationValue,
      validate,
      prevValue,
    } = config;
    const { cartTotal } = this.refs;
    let newQuantity = quantity;
    const today = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

    fetch("/cart.js")
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        if (quantity > 0 && validate) {
          const cartConfig = {};
          const bundleVariantsElement = document.querySelector("#bundle-variants");
          const jsonText = bundleVariantsElement
            ? (bundleVariantsElement.textContent || "{}")
                .trim()
                .replace(",}", "}")
            : "{}";
          const inventory_value = JSON.parse(jsonText);

          data.items.forEach((item) => {
            if (item.properties._flavors) {
              const propertyFlavors = item.properties._flavors.split("//");
              const flavorsId = item.properties._flavorIds.split(",");
              propertyFlavors.forEach((flavor, index) => {
                const flavorQty = flavor.split(":")[1];
                const flavorId = flavorsId[index];
                const decodedFlavorName = this.#decodeHTMLEntities(flavor.split(":")[0]);
                const key = `${flavorId}:${item.properties.deliveryLocalLocation}`;
                const keyName = `${decodedFlavorName}:${
                  item.properties.deliveryLocalLocation
                }`;
                cartConfig[key] = (cartConfig[key] || 0) + Number(flavorQty);
                cartConfig[keyName] =
                  (cartConfig[keyName] || 0) + Number(flavorQty);
              });
            } else {
              const key = `${item.variant_id}:${item.properties.deliveryLocalLocation}`;
              const decodedTitle = this.#decodeHTMLEntities(item.title);
              const keyName = `${decodedTitle}:${item.properties.deliveryLocalLocation}`;
              cartConfig[key] = (cartConfig[key] || 0) + Number(item.quantity);
              cartConfig[keyName] =
                (cartConfig[keyName] || 0) + Number(item.quantity);
            }
          });

          const quantityInput = document.querySelector(`.cart-items__quantity [name="updates[]"][data-variant-id="${variantId}"]`);
          const isMerch = quantityInput?.dataset?.merch === 'true';
          
          // Only check inventory if merch OR if selected date is today
          const shouldCheckInventory = isMerch || (date && today === date);
          
          let availableItem = true;
          if (shouldCheckInventory && variantId === CUSTOM_DOZEN_VARIANT_ID) {
            const flavorsList = flavors.split("//");
            flavorsList.forEach((flavor) => {
              const flavorName = this.#decodeHTMLEntities(flavor.split(":")[0]);
              const flavorQty = flavor.split(":")[1];
              const currentInventory = inventory_value[flavorName]?.[locationValue];
              const keyName = `${flavorName}:${locationValue}`;
              const cartQty = cartConfig[keyName];
              if (currentInventory && +flavorQty * +quantity + +cartQty > +currentInventory) {
                availableItem = false;
              }
            });
          } else if (shouldCheckInventory && variantId === HALF_DOZEN_VARIANT_ID) {
            // Check inventory for half dozen flavors
            const flavorsList = flavors.split("//");
            flavorsList.forEach((flavor) => {
              const flavorName = this.#decodeHTMLEntities(flavor.split(":")[0]);
              const flavorQty = flavor.split(":")[1];
              const currentInventory = inventory_value[flavorName]?.[locationValue];
              const keyName = `${flavorName}:${locationValue}`;
              const cartQty = cartConfig[keyName];
              if (currentInventory && +flavorQty * +quantity + +cartQty > +currentInventory) {
                availableItem = false;
              }
            });
          } else if (shouldCheckInventory) {
            if (!isFlavor || (isFlavor && today === date)) {
              const inventoryMap = {
                georgetown: inventoryGeorgetown,
                bethesda: inventoryBethesda,
                nationwide: inventoryNationwide,
              };

              const cartKey = `${variantId}:${locationValue}`;
              const currentInventory = inventoryMap[locationValue];
              const currentCartQuantity = cartConfig[cartKey];

              if (currentCartQuantity && currentInventory) {
                if (currentCartQuantity >= currentInventory) {
                  availableItem = false;
                }
              }
            }
          }
          if (!availableItem) {
            // Re-render cart to restore previous state
            sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
              const errorContainer = document.querySelector(`.cart-item-error-container-${line}`);
              if (errorContainer) {
                errorContainer.classList.remove("hidden");
                setTimeout(() => {
                  errorContainer.classList.add("hidden");
                }, 3000);
              }
            });
            
            resetShimmer(this);
            this.#enableCartItems();
            cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
            return;
          }
        }

        const cartItemsComponents = document.querySelectorAll("cart-items-component");
        const sectionsToUpdate = new Set([this.sectionId]);
        cartItemsComponents.forEach((item) => {
          if (item instanceof HTMLElement && item.dataset.sectionId) {
            sectionsToUpdate.add(item.dataset.sectionId);
          }
        });

        const fetchConfigBody = {
          line: line,
          quantity: newQuantity,
          sections: Array.from(sectionsToUpdate).join(","),
          sections_url: window.location.pathname,
        };

        const body = JSON.stringify(fetchConfigBody);

        cartTotal?.shimmer();

        fetch(`${Theme.routes.cart_change_url}`, fetchConfig("json", { body }))
          .then((response) => {
            return response.text();
          })
          .then((responseText) => {
            const parsedResponseText = JSON.parse(responseText);

            resetShimmer(this);

            if (parsedResponseText.errors) {
              this.#handleCartError(line, parsedResponseText);
              return;
            }

            const newSectionHTML = new DOMParser().parseFromString(
              parsedResponseText.sections[this.sectionId],
              "text/html"
            );

            // Grab the new cart item count from a hidden element
            const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
            const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

            if (newSectionHTML.querySelector(".main-cart-component")) {
              if (newQuantity > 0) {
                this.checkBundle();
              } else {
                document.dispatchEvent(
                  new CustomEvent("cart-items-updated", {
                    detail: { newSectionHTML: newSectionHTML },
                  })
                );
              }
            } else {
              this.dispatchEvent(
                new CartUpdateEvent({}, this.sectionId, {
                  itemCount: newCartItemCount,
                  source: "cart-items-component",
                  sections: parsedResponseText.sections,
                })
              );
              morphSection(
                this.sectionId,
                parsedResponseText.sections[this.sectionId]
              );
            }

            // Reinitialize loyalty widgets after cart update
            this.#reinitializeLoyaltyWidgets();
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.#enableCartItems();
            cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
          });
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */

  #handleCartError = (line, parsedResponseText) => {
    // Re-render cart to restore previous state
    sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
      const cartItemError = this.refs[`cartItemError-${line}`];
      const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

      if (cartItemError instanceof HTMLElement) {
        cartItemError.textContent = parsedResponseText.errors;
      }
      
      if (cartItemErrorContainer instanceof HTMLElement) {
        cartItemErrorContainer.classList.remove("hidden");
        setTimeout(() => {
          cartItemErrorContainer.classList.add("hidden");
        }, 3000);
      }
    });
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    this.checkBundle();

    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    setTimeout(() => {
      this.setMultipleRecipientsClick();
      
      const cartRecommended = document.querySelector('cart-recommended');
      if (cartRecommended) {
        if (!customElements.get("cart-recommended")) {
          customElements.define("cart-recommended", CartRecommended);
        } else {
          customElements.upgrade(cartRecommended);
        }
      }
    }, 2000);
  };

  checkBundle() {
    const self = this;
    let cartType;
    fetch("/cart.js")
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        if (data.items.length === 0) return;
        cartType = data.attributes.type;
        const cartObject = {};
        let totalHalfDozen = 0;
        data.items.reverse().forEach((item) => {
          if (
            item.handle !== "custom-dozen" &&
            item.handle !== "half-dozen" &&
            item.properties._itemType === "flavor"
          ) {
            const deliveryDate = item.properties.deliveryDate || "";
            const deliveryZip = item.properties.deliveryZip || "";
            const deliveryLocalLocation =
              item.properties.deliveryLocalLocation || "";
            const deliveryLocalType = item.properties.deliveryLocalType || "";

            const groupKey = `${deliveryDate}|${deliveryZip}|${deliveryLocalLocation}|${deliveryLocalType}`;

            if (!cartObject[groupKey]) {
              cartObject[groupKey] = [];
              cartObject[groupKey].push(item);
            } else {
              cartObject[groupKey].push(item);
            }
          }
          if (item.handle === "half-dozen") totalHalfDozen += item.quantity;
        });
        if (cartType === "nationwide") {
          Object.keys(cartObject).map((groupKey) => {
            let groupQty = 0;
            cartObject[groupKey].forEach((item) => {
              groupQty += item.quantity;
            });

            if (groupQty >= 12) {
              self.generateBundle(cartObject[groupKey], cartType);
            } else {
              if (document.querySelector(".main-cart-component")) {
                document.dispatchEvent(new CustomEvent("cart-items-updated"));
              }
            }
          });
        } else {
          Object.keys(cartObject).map((groupKey) => {
            let groupQty = 0;
            cartObject[groupKey].forEach((item) => {
              groupQty += item.quantity;
            });
            if (groupQty >= 6) {
              self.generateHalfDozen(cartObject[groupKey], cartType);
            } else {
              if (document.querySelector(".main-cart-component")) {
                document.dispatchEvent(new CustomEvent("cart-items-updated"));
              }
            }
          });
        }

        if (totalHalfDozen > 1) {
          self.checkDoubleDozen();
        }

        if (Object.keys(cartObject).length === 0 && totalHalfDozen === 0) {
          if (document.querySelector(".main-cart-component")) {
            document.dispatchEvent(new CustomEvent("cart-items-updated"));
          }
        }
      });
  }

  generateHalfDozen(items, cartType) {
    let totalQty = 0;
    let flavors = [];
    let ids = [];
    let bundledFlavors = [];
    let partBundledItem;
    let partBundledQty;

    items.forEach((item) => {
      if (totalQty < 6) {
        if (totalQty + item.quantity <= 6) {
          totalQty += item.quantity;
          flavors.push(`${item.title}:${item.quantity}`);
          ids.push(item.variant_id);
          bundledFlavors.push(item);
        } else {
          partBundledItem = item;
          partBundledQty = 6 - totalQty;
          totalQty = 6;
          flavors.push(`${item.title}:${partBundledQty}`);
          ids.push(item.variant_id);
        }
      }
    });

    const updates = {};
    let additionalBundlesQty = 0;
    let remainder = 0;

    bundledFlavors.forEach((item) => {
      updates[item.key] = 0;
    });

    if (partBundledItem && partBundledItem.quantity - partBundledQty > 6) {
      additionalBundlesQty = Math.floor((partBundledItem.quantity - partBundledQty) / 6);
      remainder = (partBundledItem.quantity - partBundledQty) % 6;
    }
    if (partBundledItem && additionalBundlesQty === 0) {
      updates[partBundledItem.key] = partBundledItem.quantity - partBundledQty;
    } else if (partBundledItem && additionalBundlesQty > 0) {
      updates[partBundledItem.key] = remainder;
    }

    this.updateCart(HALF_DOZEN_VARIANT_ID, updates, items, flavors, ids, partBundledItem, additionalBundlesQty, cartType, 6);
  }

  generateBundle(items, cartType) {
    let totalQty = 0;
    let flavors = [];
    let ids = [];
    let bundledFlavors = [];
    let partBundledItem;
    let partBundledQty;

    items.forEach((item) => {
      if (totalQty < 12) {
        if (totalQty + item.quantity <= 12) {
          totalQty += item.quantity;
          flavors.push(`${item.title}:${item.quantity}`);
          ids.push(item.variant_id);
          bundledFlavors.push(item);
        } else {
          partBundledItem = item;
          partBundledQty = 12 - totalQty;
          flavors.push(`${item.title}:${partBundledQty}`);
          ids.push(item.variant_id);
        }
      }
    });

    const updates = {};
    let additionalBundlesQty = 0;
    let remainder = 0;

    bundledFlavors.forEach((item) => {
      updates[item.key] = 0;
    });

    if (partBundledItem && partBundledItem.quantity - partBundledQty > 12) {
      additionalBundlesQty = Math.floor(
        (partBundledItem.quantity - partBundledQty) / 12
      );
      remainder = (partBundledItem.quantity - partBundledQty) % 12;
    }
    if (partBundledItem && additionalBundlesQty === 0) {
      updates[partBundledItem.key] = partBundledItem.quantity - partBundledQty;
    } else if (partBundledItem && additionalBundlesQty > 0) {
      updates[partBundledItem.key] = remainder;
    }
    this.updateCart(CUSTOM_DOZEN_VARIANT_ID, updates, items, flavors, ids, partBundledItem, additionalBundlesQty, cartType, 12);
  }

  updateCart(id, updates, items, flavors, ids, partBundledItem, additionalBundlesQty, cartType, qty) {
    fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ updates }),
    })
      .then((res) => res.json())
      .then((cart) => {
        const firstItem = items[0];
        const deliveryProperties = {
          _availableMonth: firstItem.properties._availableMonth || "",
          _uniqueId: Date.now().toString(),
          deliveryDate: firstItem.properties.deliveryDate || "",
          deliveryZip: firstItem.properties.deliveryZip || "",
          deliveryTime: firstItem.properties.deliveryTime || "",
          deliveryLocalLocation: firstItem.properties.deliveryLocalLocation || "",
          deliveryLocalType: firstItem.properties.deliveryLocalType || "",
        };

        const body = {
          items: [
            {
              id: id,
              quantity: 1,
              properties: {
                _flavors: flavors.join("//"),
                _flavorIds: ids.join(","),
                _type: cartType,
                ...deliveryProperties,
              },
              attributes: {
                activeType: cartType,
              },
            },
          ],
        };
        if (additionalBundlesQty > 0) {
          body.items.push({
            id: id,
            quantity: additionalBundlesQty,
            properties: {
              _flavors: `${partBundledItem.title}:${qty}`,
              _type: cartType,
              ...deliveryProperties,
            },
            attributes: {
              activeType: cartType,
            },
          });
        }

        fetch(Theme.routes.cart_add_url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              self.dispatchEvent(
                new CartErrorEvent(
                  form.getAttribute("id") || "",
                  response.message,
                  response.description,
                  response.errors
                )
              );
            } else {
              if (cartType === "nationwide") {
                if (!document.querySelector(".main-cart-component")) {
                  document.dispatchEvent(new CartAddEvent());
                } else {
                  document.dispatchEvent(new CustomEvent("cart-items-updated"));
                }
              } else {
                this.checkDoubleDozen();
              }

              // Update loyalty points widget
              if (typeof window.updateLoyaltyPointsText === "function") {
                window.updateLoyaltyPointsText();
              }
            }
          });
      });
  }

  checkDoubleDozen() {
    let dozenObject = {};
    let totalHalfDozen = 0;
    fetch("/cart.js")
      .then((response) => response.json())
      .then((data) => {
        data.items.forEach((item) => {
          if (item.handle === "half-dozen") {
            totalHalfDozen += item.quantity;
            const deliveryDate = item.properties.deliveryDate || "";
            const deliveryZip = item.properties.deliveryZip || "";
            const deliveryLocalLocation = item.properties.deliveryLocalLocation || "";
            const deliveryLocalType = item.properties.deliveryLocalType || "";

            const groupKey = `${deliveryDate}|${deliveryZip}|${deliveryLocalLocation}|${deliveryLocalType}`;
            if (!dozenObject[groupKey]) {
              dozenObject[groupKey] = [];
              dozenObject[groupKey].push(item);
            } else {
              dozenObject[groupKey].push(item);
            }
          }
        });
        if (Object.keys(dozenObject).length > 0 && totalHalfDozen > 1) {
          Object.keys(dozenObject).forEach((key) => {
            let items = dozenObject[key];
            let updates = {};
            let bundledItems = [];
            let remainderItem = [];
            items.forEach((item) => {
              if (item.quantity > 2) {
                updates[item.key] = item.quantity - Math.floor(item.quantity / 2);
                bundledItems.push({
                  items: [item],
                  quantity: Math.floor(item.quantity / 2),
                });
                if (item.quantity % 2 !== 0) {
                  remainderItem.push({
                    id: item.id,
                    quantity: 1,
                    item: item,
                  });
                }
              } else if (item.quantity === 2) {
                updates[item.key] = 0;
                bundledItems.push({
                  items: [item],
                  quantity: 1,
                });
              } else if (item.quantity === 1) {
                if (remainderItem.length > 0) {
                  updates[item.key] = 0;
                  updates[remainderItem[0].item.key] = 0;

                  bundledItems.push({
                    items: [item, remainderItem[0].item],
                    quantity: 1,
                  });
                  remainderItem = [];
                } else {
                  remainderItem.push({
                    id: item.id,
                    quantity: 1,
                    item: item,
                  });
                }
              }
            });

            if (bundledItems.length > 0) {
              const itemsToAdd = [];
              bundledItems.forEach((item) => {
                const multiplyFlavors = (flavorsString, multiplier) => {
                  return flavorsString.split('//').map(flavor => {
                    const [name, qty] = flavor.split(':');
                    return `${name}:${parseInt(qty) * multiplier}`;
                  }).join('//');
                };

                const flavors = item.items[1] 
                  ? item.items[0].properties._flavors + "//" + item.items[1].properties._flavors 
                  : multiplyFlavors(item.items[0].properties._flavors, 2);
                
                const flavorIds = item.items[1]
                  ? item.items[0].properties._flavorIds + "," + item.items[1].properties._flavorIds
                  : item.items[0].properties._flavorIds;

                itemsToAdd.push({
                  id: CUSTOM_DOZEN_VARIANT_ID,
                  quantity: item.quantity,
                  properties: {
                    _flavors: flavors,
                    _flavorIds: flavorIds,
                    _type: item.items[0].properties._type,
                    _availableMonth: item.items[0].properties._availableMonth,
                    _uniqueId: Date.now().toString(),
                    deliveryDate: item.items[0].properties.deliveryDate,
                    deliveryZip: item.items[0].properties.deliveryZip,
                    deliveryTime: item.items[0].properties.deliveryTime,
                    deliveryLocalLocation: item.items[0].properties.deliveryLocalLocation,
                    deliveryLocalType: item.items[0].properties.deliveryLocalType,
                  },
                });
              });

              fetch("/cart/update.js", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                },
                body: JSON.stringify({ updates }),
              })
                .then((res) => res.json())
                .then((data) => {
                  const body = {
                    items: itemsToAdd,
                  };
                  fetch(Theme.routes.cart_add_url, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify(body),
                  })
                    .then((res) => res.json())
                    .then((data) => {
                      if (!document.querySelector(".main-cart-component")) {
                        document.dispatchEvent(new CartAddEvent());
                      } else {
                        document.dispatchEvent(
                          new CustomEvent("cart-items-updated")
                        );
                      }
                    });
                });
            }
          });
        } else {
          if (!document.querySelector(".main-cart-component")) {
            document.dispatchEvent(new CartAddEvent());
          } else {
            document.dispatchEvent(new CustomEvent("cart-items-updated"));
          }
        }
      });
  }
  /**
   * Reinitializes loyalty widgets after cart update
   */
  #reinitializeLoyaltyWidgets() {
    if (typeof window.updateLoyaltyPointsText === "function") {
      window.updateLoyaltyPointsText();
    }
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }

  setMultipleRecipientsClick() {
    document
      .querySelector(".js-multiple-recipients")
      ?.addEventListener("click", (e) => this.handleMultipleRecipients(e));
  }

  handleMultipleRecipients(e) {
    localStorage.removeItem("delivery-date");
    localStorage.removeItem("delivery-zip");
    localStorage.removeItem("delivery-time");
    localStorage.removeItem("delivery-type");
    localStorage.removeItem("delivery-location");
    location.href = e.currentTarget.dataset.link;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
